/**
 * @description Outreach container page with Activity/Templates tab navigation
 *
 * Key features:
 * - Custom tab nav (Activity | Templates) matching CompanyTabs/Transactions pattern
 * - URL-driven tab selection via query param (?tab=activity|templates)
 * - Templates tab only visible if user has outreach.manage permission
 * - Permission-gated: requires outreach.view for any access
 * - React.Suspense wrapper for lazy-loaded child pages
 *
 * @dependencies
 * - ActivityPage: Sent email log with filters + stats
 * - TemplatesPage: Template editor + live preview
 * - PermissionGate: RBAC UI gating
 *
 * @notes
 * - Uses custom tab buttons (not Headless UI Tab.Group) to avoid controlled-mode
 *   re-render loop with dynamic Tab children (HUI v1.x bug causing React error #185)
 * - Matches CompanyTabs/Transactions tab pattern: URL single source of truth,
 *   permission-filtered tabs, conditional content rendering
 * - Employee users see Activity only (no Templates tab)
 */

import React, { useMemo, useCallback, Suspense, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionContext';
import PermissionGate from '../shared/PermissionGate';

const ActivityPage = lazy(() => import('./ActivityPage'));
const TemplatesPage = lazy(() => import('./TemplatesPage'));

const TabFallback = () => (
  <div className="flex items-center justify-center py-20">
    <div className="text-center">
      <div className="animate-spin w-8 h-8 border-4 border-brand border-t-transparent rounded-full mx-auto mb-3" />
      <p className="text-sm text-gray-500 font-medium">Loading...</p>
    </div>
  </div>
);

const OutreachPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isLoaded } = usePermissions();

  const canManageOutreach = isLoaded && hasPermission('outreach.manage');

  const tabs = useMemo(() => {
    const list = [{ key: 'activity', label: 'Activity' }];
    if (canManageOutreach) {
      list.push({ key: 'templates', label: 'Templates' });
    }
    return list;
  }, [canManageOutreach]);

  // Derive active tab from URL — single source of truth
  const activeTab = useMemo(() => {
    const tabKey = searchParams.get('tab') || 'activity';
    return tabs.some(t => t.key === tabKey) ? tabKey : 'activity';
  }, [searchParams, tabs]);

  const handleTabClick = useCallback((tabKey) => {
    setSearchParams({ tab: tabKey }, { replace: true });
  }, [setSearchParams]);

  return (
    <PermissionGate permission="outreach.view" fallback={
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Access Denied</h3>
        <p className="text-gray-500 mt-2">You don&apos;t have permission to view outreach.</p>
      </div>
    }>
      {!isLoaded ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-brand border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">Loading...</p>
          </div>
        </div>
      ) : (
      <div className="max-w-[2000px] w-full mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Outreach</h1>
        </div>

        {/* Tabs — matching CompanyTabs/Transactions styling */}
        <div className="border-b mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabClick(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-brand text-brand'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <Suspense fallback={<TabFallback />}>
          {activeTab === 'activity' && <ActivityPage />}
          {activeTab === 'templates' && canManageOutreach && <TemplatesPage />}
        </Suspense>
      </div>
      )}
    </PermissionGate>
  );
};

export default OutreachPage;
